import React, { FunctionComponent, useEffect, useState } from 'react'
import { Select } from 'antd'
import { MAX_KEYWORDS_COUNT, MIN_KEYWORD_LENGTH } from '../../../constants'
import { TKeyword } from '../../../context/types'
import { useKeywordsContext } from '../../../context/keyword'

type TProps = {
	selectedKeywords?: string[]
	selectedOptions?: TKeyword[]
	onChangeKeywords?: (value: any) => void
	notFoundContent?: string
}

const PRESS_ENTER_TEXT = 'Press Enter to add a new keyword'
const allowSymbols = [' ', '/', '-']
const chkInvalidCharRegex = new RegExp(/^[A-Za-z0-9? //-]+$/)

const KeywordsSelect: FunctionComponent<TProps> = (props) => {
	const { selectedKeywords, selectedOptions, onChangeKeywords, notFoundContent } = props
	const { keywords, actions: keywordsAction } = useKeywordsContext()

	const [suggestionOptions, setSuggestionOptions] = useState<TKeyword[]>([])
	const [options, setOptions] = useState<TKeyword[]>([])
	const [searchValue, setSearchValue] = useState<string>('')

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (searchValue.length >= 1) {
				const keywordIds = selectedOptions.map((k) => k.id)
				keywordsAction.allLists(searchValue, keywordIds)
			}
		}, 300)
		return () => {
			clearTimeout(delayDebounceFn)
		}
	}, [searchValue])

	useEffect(() => {
		const filteredOptions = suggestionOptions.filter(
			(k1) => !selectedKeywords.map((k2) => k2.toUpperCase()).includes(k1.keyword.toUpperCase())
		)
		setOptions(filteredOptions)
	}, [suggestionOptions, selectedKeywords])

	useEffect(() => {
		setSuggestionOptions(keywords.keywords)
	}, [keywords.keywords])

	const handleSearch = (value: string) => {
		value.trim().length >= 1 ? setSearchValue(value.trim()) : setOptions([])
	}

	const handleChange = (changedValue: string[]) => {
		const lastKeywordInx = changedValue.length - 1
		const lastKeyword = changedValue[lastKeywordInx]

		if (changedValue.length < selectedKeywords.length) {
			const deletedKey = selectedKeywords.find((k1) => !changedValue.some((k2) => k2 === k1))
			const inx = selectedOptions.findIndex((k) => k.keyword.toUpperCase() === deletedKey.toUpperCase())

			if (inx !== -1 && selectedOptions[inx]) selectedOptions.splice(inx, 1)

			updateFormValues(changedValue)
		}

		if (changedValue.length <= MAX_KEYWORDS_COUNT && changedValue.length > selectedKeywords.length) {
			const inx = suggestionOptions.findIndex((k) => k.keyword.toUpperCase() === lastKeyword.toUpperCase())

			if (inx !== -1 && suggestionOptions[inx]) selectedOptions.push(suggestionOptions[inx])

			validateAndChkDuplicate(lastKeyword)
		}
	}

	const handleInputKeyDown = (event: any) => {
		const isValid = chkInvalidCharRegex.test(event.key)
		if (!isValid) event.preventDefault()

		if (event.key === 'Enter' && selectedKeywords.length < MAX_KEYWORDS_COUNT) {
			validateAndChkDuplicate(strToUpperCase(event.target.value))
			event.target.blur()
			event.target.focus()
		}
	}

	const validateAndChkDuplicate = (keyword: string) => {
		if (keyword.trim().length <= MIN_KEYWORD_LENGTH) return

		const inx = selectedKeywords.findIndex((k) => k.toUpperCase() === keyword.toUpperCase())
		if (inx !== -1) return

		updateFormValues([...selectedKeywords, keyword])
	}

	const updateFormValues = (keywords: string[]) => {
		onChangeKeywords({ keywords: keywords })
	}

	return (
		<Select
			mode="multiple"
			value={selectedKeywords}
			onSearch={handleSearch}
			onInputKeyDown={handleInputKeyDown}
			onChange={handleChange}
			notFoundContent={searchValue ? PRESS_ENTER_TEXT : notFoundContent}
		>
			{options.map((item) => (
				<Select.Option key={item.keyword} value={item.keyword}>
					{item.keyword}
				</Select.Option>
			))}
		</Select>
	)
}

export default KeywordsSelect

function strToUpperCase(value: string) {
	if (!value) return ''
	return value
		.trim()
		.split('')
		.map((val, inx, arr) => {
			if (inx === 0 || allowSymbols.indexOf(arr[inx - 1]) !== -1) {
				return val.toUpperCase()
			}
			return val
		})
		.join('')
}
